import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useActivityFeed, type ActivityFeedItem } from '../../api/reports.api';

const PAGE_TITLES: Record<string, string> = {
  '/dashboard':  'Dashboard',
  '/campaigns':  'Campaigns',
  '/audiences':  'Audiences',
  '/inbox':      'Inbox',
  '/templates':  'Templates',
  '/reports':    'Reports',
  '/settings':   'Settings',
  '/components': 'Component Library',
};

function getTitle(pathname: string): string {
  const base = '/' + pathname.split('/')[1];
  return PAGE_TITLES[base] ?? 'Engage';
}

export function Topbar() {
  const { pathname } = useLocation();
  const title = getTitle(pathname);

  return (
    <header style={{
      height: 'var(--topbar-height)',
      backgroundColor: 'var(--topbar-bg)',
      borderBottom: '1px solid var(--topbar-border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 var(--space-6)',
      flexShrink: 0,
      position: 'sticky',
      top: 0,
      zIndex: 'var(--z-sticky)',
    }}>
      {/* Left: page title */}
      <h1 style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xl)',
        fontWeight: 'var(--font-weight-bold)',
        color: 'var(--color-text-brand)',
        letterSpacing: 'var(--tracking-tight)',
      }}>
        {title}
      </h1>

      {/* Right: notifications */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <NotificationsBell />
      </div>
    </header>
  );
}

/* ── Notifications ──
 * In-app notifications are sourced from the activity feed (sends, opens,
 * replies, bounces, unsubscribes). The bell shows an unread badge and opens
 * a dropdown listing recent events; "since last opened" is tracked locally
 * so the badge clears once the user has looked. */

const NOTIFS_SEEN_KEY = 'engage:notifications:lastSeenAt';

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [lastSeenAt, setLastSeenAt] = useState<number>(() => {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(NOTIFS_SEEN_KEY) : null;
    return raw ? Number(raw) : 0;
  });
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useActivityFeed({ limit: 12 });
  const items = data ?? [];

  const unreadCount = useMemo(
    () => items.filter((i) => new Date(i.occurred_at).getTime() > lastSeenAt).length,
    [items, lastSeenAt],
  );

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markSeen = () => {
    const now = Date.now();
    setLastSeenAt(now);
    if (typeof localStorage !== 'undefined') localStorage.setItem(NOTIFS_SEEN_KEY, String(now));
  };

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      // Opening the panel clears the unread badge.
      if (next) markSeen();
      return next;
    });
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        aria-label="Notifications"
        aria-expanded={open}
        onClick={toggle}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 'var(--avatar-size-md)',
          height: 'var(--avatar-size-md)',
          border: '1px solid var(--color-border-default)',
          borderRadius: 'var(--radius-input)',
          backgroundColor: open ? 'var(--color-surface-muted)' : 'transparent',
          cursor: 'pointer',
          color: 'var(--color-text-muted)',
          transition: 'background-color var(--transition-base)',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-muted)')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = open ? 'var(--color-surface-muted)' : 'transparent')}
      >
        <IconBell size={16} />
        {unreadCount > 0 && (
          <span
            aria-label={`${unreadCount} unread notifications`}
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '16px',
              height: '16px',
              padding: '0 4px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--color-danger, #dc2626)',
              color: 'var(--color-text-inverse)',
              fontFamily: 'var(--font-sans)',
              fontSize: '10px',
              fontWeight: 'var(--font-weight-bold)',
              lineHeight: '16px',
              textAlign: 'center',
              boxShadow: '0 0 0 2px var(--topbar-bg)',
            }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--space-2))',
            right: 0,
            width: '360px',
            maxHeight: '440px',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--card-radius)',
            boxShadow: 'var(--shadow-lg, 0 12px 28px rgba(0,0,0,0.16))',
            overflow: 'hidden',
            zIndex: 'var(--z-dropdown, 1000)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-3) var(--space-4)',
              borderBottom: '1px solid var(--color-border-default)',
            }}
          >
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-brand)' }}>
              Notifications
            </span>
            <Link
              to="/reports"
              onClick={() => setOpen(false)}
              style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-link)', textDecoration: 'none' }}
            >
              View all →
            </Link>
          </div>

          <div style={{ overflowY: 'auto' }}>
            {isLoading && items.length === 0 ? (
              <p style={{ padding: 'var(--space-4)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
                Loading…
              </p>
            ) : items.length === 0 ? (
              <p style={{ padding: 'var(--space-5) var(--space-4)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                You're all caught up. Nothing new right now.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {items.map((item, i) => (
                  <NotificationRow key={item.id} item={item} isFirst={i === 0} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface NotifVisual {
  icon: React.ReactNode;
  iconBg: string;
  iconFg: string;
  verb: string;
}

function notifVisualFor(eventType: string): NotifVisual {
  switch (eventType) {
    case 'SENT':
      return { icon: <IconSend size={12} />, iconBg: 'var(--color-blue-50)', iconFg: 'var(--color-brand-blue)', verb: 'sent' };
    case 'DELIVERED':
      return { icon: <IconCheck size={12} />, iconBg: 'var(--color-success-bg)', iconFg: 'var(--color-success)', verb: 'delivered' };
    case 'OPENED':
      return { icon: <IconInbox size={12} />, iconBg: 'var(--color-blue-50)', iconFg: 'var(--color-brand-blue)', verb: 'opened' };
    case 'CLICKED':
      return { icon: <IconBell size={12} />, iconBg: 'var(--color-indigo-50)', iconFg: 'var(--color-brand-indigo)', verb: 'clicked a link in' };
    case 'REPLIED':
      return { icon: <IconInbox size={12} />, iconBg: 'var(--color-success-bg)', iconFg: 'var(--color-success)', verb: 'replied to' };
    case 'BOUNCED':
      return { icon: <IconAlert size={12} />, iconBg: 'var(--color-warning-bg)', iconFg: 'var(--color-warning)', verb: 'bounced from' };
    case 'UNSUBSCRIBED':
      return { icon: <IconAlert size={12} />, iconBg: 'var(--color-warning-bg)', iconFg: 'var(--color-warning)', verb: 'unsubscribed from' };
    case 'SPAM_REPORTED':
      return { icon: <IconAlert size={12} />, iconBg: 'var(--color-warning-bg)', iconFg: 'var(--color-warning)', verb: 'reported as spam' };
    default:
      return { icon: <IconBell size={12} />, iconBg: 'var(--color-surface-muted)', iconFg: 'var(--color-text-muted)', verb: eventType.toLowerCase() };
  }
}

function NotificationRow({ item, isFirst }: { item: ActivityFeedItem; isFirst: boolean }) {
  const v = notifVisualFor(item.event_type);
  const actor = item.contact_email ?? 'A contact';
  const campaignName = item.campaign_name ? `“${item.campaign_name}”` : 'a campaign';
  const body = (
    <li
      style={{
        padding: 'var(--space-3) var(--space-4)',
        borderTop: isFirst ? 'none' : '1px solid var(--color-border-default)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: 'var(--radius-full)',
          backgroundColor: v.iconBg,
          color: v.iconFg,
          flexShrink: 0,
          marginTop: '2px',
        }}
      >
        {v.icon}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 'var(--leading-snug)' }}>
          <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{actor}</span>{' '}
          {v.verb} {campaignName}
        </p>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {relativeTime(new Date(item.occurred_at))}
        </span>
      </div>
    </li>
  );
  // Reply/inbox events deep-link to the inbox; everything else to the campaign.
  if (item.event_type === 'REPLIED') {
    return <Link to="/inbox" style={{ textDecoration: 'none' }}>{body}</Link>;
  }
  if (item.campaign_id) {
    return <Link to={`/campaigns/${item.campaign_id}`} style={{ textDecoration: 'none' }}>{body}</Link>;
  }
  return body;
}

function relativeTime(d: Date): string {
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return d.toLocaleDateString();
}

/* ── Icons ── */

function IconBell({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  );
}

function IconSend({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function IconInbox({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function IconCheck({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconAlert({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
